import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, AlertTriangle, PauseCircle, ExternalLink } from "lucide-react";

const euro = (n) => `€ ${Number(n || 0).toFixed(2)}`;

/**
 * Il mezzo come centro di costo.
 *
 * Le tre colonne che contano sono il costo degli ultimi dodici mesi, il costo
 * per chilometro o per ora, e da quanti giorni la macchina non va in cantiere.
 * Il costo unitario esce solo quando ci sono almeno due letture del contatore
 * e i conti tornano: meglio una casella vuota che un numero inventato.
 */
export default function AnalisiCostiMezzi() {
  const [dati, setDati] = useState(null);
  const [cerca, setCerca] = useState("");
  const [soloFermi, setSoloFermi] = useState(false);
  const [soloFuoriScala, setSoloFuoriScala] = useState(false);

  useEffect(() => {
    api.get("/equipment/analisi-costi")
      .then(({ data }) => setDati(data))
      .catch(() => toast.error("Errore caricamento analisi mezzi"));
  }, []);

  const elenco = useMemo(() => {
    if (!dati) return [];
    const q = cerca.trim().toLowerCase();
    return dati.mezzi
      .filter(m => !soloFermi || m.fermo)
      .filter(m => !soloFuoriScala || m.fuori_scala)
      .filter(m => !q || [m.identifier, m.category, m.descrizione]
        .filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [dati, cerca, soloFermi, soloFuoriScala]);

  if (!dati) return null;

  const fermi = dati.mezzi.filter(m => m.fermo).length;
  const fuoriScala = dati.mezzi.filter(m => m.fuori_scala).length;

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="font-display text-lg font-bold">Il mezzo come centro di costo</h3>
          <p className="text-xs text-slate-500 mt-1">
            Ordinati per quanto sono costati negli ultimi dodici mesi.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9 h-10" placeholder="Cerca targa, categoria, modello..."
                   value={cerca} onChange={(e) => setCerca(e.target.value)} />
          </div>
          <button type="button" onClick={() => setSoloFermi(v => !v)}
                  className={`px-3 py-2 rounded-full text-xs font-medium border transition ${
                    soloFermi ? "bg-orange-500 text-white border-orange-500"
                              : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            Fermi da {dati.giorni_fermo}+ giorni ({fermi})
          </button>
          <button type="button" onClick={() => setSoloFuoriScala(v => !v)}
                  className={`px-3 py-2 rounded-full text-xs font-medium border transition ${
                    soloFuoriScala ? "bg-orange-500 text-white border-orange-500"
                                   : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            Fuori scala ({fuoriScala})
          </button>
        </div>

        <div className="border border-slate-200 rounded overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left p-3">Mezzo</th>
                <th className="text-right p-3">Ultimi 12 mesi</th>
                <th className="text-right p-3">Totale storico</th>
                <th className="text-right p-3">Costo unitario</th>
                <th className="text-left p-3">Utilizzo</th>
                <th className="text-left p-3">Ultimo impiego</th>
              </tr>
            </thead>
            <tbody>
              {elenco.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500">Nessun mezzo</td></tr>
              )}
              {elenco.map(m => (
                <tr key={m.equipment_id}
                    className={`border-t border-slate-100 ${m.fuori_scala ? "bg-amber-50/60" : ""}`}>
                  <td className="p-3">
                    <Link to={`/attrezzature/${m.equipment_id}`}
                          className="font-mono font-semibold hover:text-orange-600 inline-flex items-center gap-1">
                      {m.identifier} <ExternalLink className="w-3 h-3 opacity-50" />
                    </Link>
                    <div className="text-[11px] text-slate-500">
                      {m.category}{m.descrizione ? ` · ${m.descrizione}` : ""}
                    </div>
                  </td>

                  <td className="p-3 text-right">
                    <div className="font-semibold">{euro(m.costo_12_mesi)}</div>
                    {m.fuori_scala && (
                      <div className="text-[10px] text-amber-800 flex items-center justify-end gap-1 mt-0.5">
                        <AlertTriangle className="w-3 h-3" />
                        gli altri {euro(m.tipico_categoria_12_mesi)}
                      </div>
                    )}
                  </td>

                  <td className="p-3 text-right text-slate-600">
                    {euro(m.costo_totale)}
                    <div className="text-[10px] text-slate-400">
                      {m.interventi} intervent{m.interventi === 1 ? "o" : "i"}
                    </div>
                  </td>

                  <td className="p-3 text-right">
                    {m.euro_per_km != null && (
                      <div className="font-semibold">€ {m.euro_per_km.toFixed(3)}/km</div>
                    )}
                    {m.euro_per_ora != null && (
                      <div className="font-semibold">€ {m.euro_per_ora.toFixed(2)}/ora</div>
                    )}
                    {m.euro_per_km == null && m.euro_per_ora == null && (
                      <span className="text-[11px] text-slate-400">
                        {(m.km?.sospetto || m.ore?.sospetto)
                          ? "letture da verificare"
                          : "servono 2 letture"}
                      </span>
                    )}
                  </td>

                  <td className="p-3 text-[11px] text-slate-500">
                    {m.km && (
                      <div className={m.km.sospetto ? "text-amber-700" : ""}>
                        {m.km.quantita.toLocaleString("it-IT")} km
                        <span className="text-slate-400"> · {m.km.da.substring(0, 7)} → {m.km.a.substring(0, 7)}</span>
                      </div>
                    )}
                    {m.ore && (
                      <div className={m.ore.sospetto ? "text-amber-700" : ""}>
                        {m.ore.quantita.toLocaleString("it-IT")} ore
                        <span className="text-slate-400"> · {m.ore.da.substring(0, 7)} → {m.ore.a.substring(0, 7)}</span>
                      </div>
                    )}
                    {!m.km && !m.ore && <span className="text-slate-400">—</span>}
                  </td>

                  <td className="p-3 text-[11px]">
                    {m.ultimo_impiego ? (
                      <>
                        <div>{m.ultimo_impiego}</div>
                        <div className={m.fermo ? "text-amber-700 flex items-center gap-1" : "text-slate-400"}>
                          {m.fermo && <PauseCircle className="w-3 h-3" />}
                          {m.giorni_fermo} giorni fa
                        </div>
                      </>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">
                        mai pianificato
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-slate-500">
          Il costo per km e per ora si calcola fra la prima e l'ultima lettura del contatore,
          usando solo le spese di quel periodo. Registra le letture dalla scheda del mezzo:
          più letture ci sono, più il numero diventa preciso.
        </p>
      </CardContent>
    </Card>
  );
}
